const normalizeHost = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "")
    .replace(/:\d+$/, "");
};

const extractHost = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  try {
    const parsed = value.startsWith("http")
      ? new URL(value)
      : new URL(`https://${value}`);
    return normalizeHost(parsed.hostname);
  } catch (error) {
    return normalizeHost(value);
  }
};

const isDomainMatch = (currentHost, candidateHost) => {
  if (!currentHost || !candidateHost) {
    return false;
  }

  return (
    currentHost === candidateHost ||
    currentHost.endsWith(`.${candidateHost}`) ||
    candidateHost.endsWith(`.${currentHost}`)
  );
};

const getApiHost = () => {
  const apiHostRaw =
    typeof process !== "undefined" && process?.env?.VUE_APP_APIHOST
      ? process.env.VUE_APP_APIHOST
      : "";

  return extractHost(apiHostRaw);
};

const isLocalHost = (host) => {
  const normalized = normalizeHost(host);
  return ["localhost", "127.0.0.1", "0.0.0.0", "::1"].includes(normalized);
};

const getFirstSiteValue = (siteOptions = []) => {
  for (const option of siteOptions) {
    const value = option?.value ?? option?.pk;
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  return null;
};

const DOMAIN_BRAND_MAPPINGS = [
  { keyword: "drukujnajtaniej", brandName: "DrukujNajtaniej" },
  { keyword: "achjoj", brandName: "Achjoj" },
  { keyword: "caffeprint", brandName: "CaffePrint" },
];

const DEFAULT_BRAND_NAME = "CaffePrint";

export const getSiteValueFromCurrentUrl = (siteOptions = []) => {
  if (typeof window === "undefined" || !Array.isArray(siteOptions)) {
    return null;
  }

  const currentHost = extractHost(
    window.location.hostname || window.location.href
  );
  const apiHost = getApiHost();
  const hostsToMatch = [apiHost, currentHost].filter(Boolean);

  if (hostsToMatch.length === 0) {
    return null;
  }

  for (const option of siteOptions) {
    const candidateHost = extractHost(
      option?.name || option?.domain || option?.url
    );
    if (hostsToMatch.some((host) => isDomainMatch(host, candidateHost))) {
      return option?.value ?? option?.pk ?? null;
    }
  }

  // Local dev fallback: when running on localhost and matching is impossible,
  // pick first available site to keep domain filter usable in local env.
  if (isLocalHost(currentHost)) {
    return getFirstSiteValue(siteOptions);
  }

  return null;
};

export const hasSiteOptionValue = (siteOptions = [], selectedValue = null) => {
  if (!Array.isArray(siteOptions) || siteOptions.length === 0) {
    return false;
  }

  const selectedValues = Array.isArray(selectedValue)
    ? selectedValue
    : [selectedValue];

  const normalizedSelectedValues = selectedValues
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => String(value));

  if (normalizedSelectedValues.length === 0) {
    return false;
  }

  const optionValues = new Set(
    siteOptions
      .map((option) => option?.value ?? option?.pk)
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => String(value))
  );

  return normalizedSelectedValues.some((value) => optionValues.has(value));
};

export const getBrandNameFromCurrentDomain = () => {
  if (typeof window === "undefined") {
    return DEFAULT_BRAND_NAME;
  }

  const currentHost = extractHost(
    window.location.hostname || window.location.href
  );
  const apiHost = getApiHost();
  const hostsToMatch = [currentHost, apiHost].filter(Boolean);

  if (hostsToMatch.length === 0) {
    return DEFAULT_BRAND_NAME;
  }

  for (const host of hostsToMatch) {
    for (const mapping of DOMAIN_BRAND_MAPPINGS) {
      if (host.includes(mapping.keyword)) {
        return mapping.brandName;
      }
    }
  }

  return DEFAULT_BRAND_NAME;
};
